import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useAIBrandProfileVersions, useRestoreAIBrandProfileVersion } from '@/hooks/useAIBrandProfiles';
import type { AIBrandProfileVersion } from '@/types/ai';
import { Modal } from '@/components/ui/Modal';
import { ConfirmationModal } from '@/components/ui/ConfirmationModal';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { useFeatureLock } from '@/hooks/useFeatureLock';

/**
 * AIBrandProfileVersionHistoryModal
 *
 * Lists the automatic version snapshots the server takes on every profile
 * update (newest first) and lets the user restore an older one. Restoring is
 * itself snapshotted server-side first, so it's always undoable via another
 * restore.
 */

interface AIBrandProfileVersionHistoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileId: number | null;
  profileName?: string;
}

export const AIBrandProfileVersionHistoryModal = ({
  isOpen,
  onClose,
  profileId,
  profileName,
}: AIBrandProfileVersionHistoryModalProps) => {
  const { t } = useTranslation(['settings', 'common']);
  const { formatDateTime } = useTimezone();
  const { data: versions, isLoading } = useAIBrandProfileVersions(profileId);
  const restoreMutation = useRestoreAIBrandProfileVersion();
  const [confirmVersion, setConfirmVersion] = useState<AIBrandProfileVersion | null>(null);

  // Plan gating (brand_world): browsing versions stays open, restoring is locked (the click opens the upsell)
  const brandWorldLock = useFeatureLock('brand_world');

  const handleRestore = brandWorldLock.guard((version: AIBrandProfileVersion) => {
    setConfirmVersion(version);
  });

  const confirmRestore = () => {
    if (!confirmVersion || profileId === null) return;
    restoreMutation.mutate(
      { id: profileId, versionNumber: confirmVersion.versionNumber },
      { onSuccess: () => setConfirmVersion(null) }
    );
  };

  return (
    <>
      <Modal
        open={isOpen}
        onOpenChange={(open) => {
          if (!open) onClose();
        }}
        title={t('settings:settings_ai_profile_version_history_title', {
          defaultValue: 'Version History',
        })}
        size="lg"
      >
        {profileName && (
          <p className="text-sm text-gray-500 mb-4 -mt-2">{profileName}</p>
        )}

        {isLoading ? (
          <div className="py-12 text-center">
            <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-primary-600" />
          </div>
        ) : !versions || versions.length === 0 ? (
          <div className="py-12 text-center">
            <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50">
              <Icon name={ICONS.HISTORY} size="lg" color="text-gray-400" ariaHidden />
            </div>
            <p className="text-sm font-medium text-gray-700">
              {t('settings:settings_ai_profile_version_history_empty_title', {
                defaultValue: 'No versions yet',
              })}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              {t('settings:settings_ai_profile_version_history_empty_description', {
                defaultValue: 'A version snapshot is created automatically each time this profile is edited.',
              })}
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {versions.map((version) => (
              <li key={version.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900">
                    {t('settings:settings_ai_profile_version_number', {
                      number: version.versionNumber,
                      defaultValue: `Version ${version.versionNumber}`,
                    })}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">{formatDateTime(version.createdAt)}</p>
                  {version.note && (
                    <p className="text-xs text-gray-600 mt-1 truncate">{version.note}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => handleRestore(version)}
                  disabled={restoreMutation.isPending}
                  title={brandWorldLock.locked ? brandWorldLock.label : undefined}
                  className="shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 disabled:opacity-50 flex items-center gap-1.5"
                >
                  <Icon name={brandWorldLock.locked ? ICONS.LOCK : ICONS.UNDO} size="xs" ariaHidden />
                  {t('settings:settings_ai_profile_version_restore', { defaultValue: 'Restore' })}
                </button>
              </li>
            ))}
          </ul>
        )}
      </Modal>

      {/* Restore confirmation — nested; the shared Modal stack auto-layers it above the list modal */}
      <ConfirmationModal
        open={!!confirmVersion}
        onOpenChange={(open) => {
          if (!open) setConfirmVersion(null);
        }}
        title={t('settings:settings_ai_profile_version_restore_confirm_title', {
          defaultValue: 'Restore this version?',
        })}
        description={t('settings:settings_ai_profile_version_restore_confirm_description', {
          defaultValue:
            "This replaces the profile's current content with this version. The current state is saved automatically first, so this can always be undone.",
        })}
        variant="warning"
        confirmText={t('settings:settings_ai_profile_version_restore', { defaultValue: 'Restore' })}
        cancelText={t('common:cancel', { defaultValue: 'Cancel' })}
        onConfirm={confirmRestore}
        isLoading={restoreMutation.isPending}
      />
    </>
  );
};
